import React, { FC, useMemo, useState } from 'react';

import { format, getZonedDate } from '@eika-infrastruktur/date';
import { capitalize, formatAccountNumber } from '@eika-infrastruktur/format';
import { DetailsList, DetailsListItem } from '@eika/details-list';
import { IconTypes } from '@eika/icon';

import { Content } from '../../components/shared/Content';
import { currencyFormatter } from '../../components/shared/numberFormat';
import { useConfig } from '../../defaultConfig';
import { useAccount } from '../../fetchHooks/useAccount';
import useLoanRepaymentPlan from '../../fetchHooks/useLoanRepaymentPlan';
import { useLoanDetails } from '../../fetchHooks/useLoans';
import type { AccountNumber } from '../../generated-types';
import useAccountId from '../../hooks/queryparams/useAccountId';
import useTranslation from '../../hooks/useTranslation';
import { loansTrackingIDs } from '../../utils/gtm/trackIDs/loans';
import EditLoanDebitAccountModal from '../EditLoan/components/EditLoanDebitAccountModal';
import EditLoanPaymentDay from '../EditLoan/components/EditLoanPaymentDayModal';
import getYearOrMonthIntervalText from '../EditLoan/getYearOrMonthIntervalText';

import './LoanDetails.scss';

const labelDateFormatYear = 'd. MMMM yyyy';
const monthYearDateFormat = 'MMMM yyyy';

const dateFormatAndCapitalize = (date: string) => {
    const formatted = format(date, monthYearDateFormat);
    return capitalize(formatted);
};

const LoanDetails: FC = () => {
    const { featureToggles } = useConfig();
    const accountId = useAccountId();
    const translated = useTranslation();
    const { data: account, error, isLoading } = useAccount(accountId);
    const {
        data: loanDetails,
        isLoading: loanDetailsLoading,
        invalidateQuery,
    } = useLoanDetails(accountId, account?.additionalInfo.hasLoanDetails === true);
    const { data: loanRepaymentPlan } = useLoanRepaymentPlan(accountId);

    const getMonthOrYearText = useMemo(() => getYearOrMonthIntervalText(translated), [translated]);

    const eachYearOrMonthText = useMemo(
        () => getMonthOrYearText(loanDetails, loanRepaymentPlan),
        [loanRepaymentPlan, loanDetails, getMonthOrYearText],
    );

    const [paymentDateModalOpen, setPaymentDateModalOpen] = useState(false);
    const [debitAccountModalOpen, setDebitAccountModalOpen] = useState(false);

    const targetAccountId = accountId ?? account?.accountId ?? loanDetails?.accountId;

    const onSubmit = async () => {
        setPaymentDateModalOpen(false);
        setDebitAccountModalOpen(false);
        await invalidateQuery();
    };

    if (error && !isLoading && !loanDetailsLoading) {
        return null;
    }

    return (
        <Content className="loan-details">
            <DetailsList title={translated.accountDetails.details} isLoading={isLoading || loanDetailsLoading}>
                <DetailsListItem label={translated.loanDetails.nameOfLoan} value={account?.alias} />
                <DetailsListItem
                    label={translated.accountDetails.openedDate}
                    formatter={(value) => format(value, labelDateFormatYear)}
                    value={account?.opened}
                />
                <DetailsListItem<number>
                    show={typeof loanDetails?.initialAmount === 'number'}
                    label={translated.loanDetails.initialAmount}
                    formatter={(value) => currencyFormatter(value, account?.currency ?? 'NOK')}
                    value={loanDetails?.initialAmount}
                />
                <DetailsListItem
                    label={translated.loanDetails.plannedDownpaymentDate}
                    formatter={dateFormatAndCapitalize}
                    value={loanDetails?.lastPaymentDay}
                />
                <DetailsListItem<AccountNumber>
                    label={translated.accountNumber}
                    formatter={formatAccountNumber}
                    value={account?.accountNumber}
                />
                <DetailsListItem<AccountNumber>
                    label={translated.loanDetails.debitAccount}
                    button={{
                        title: translated.loanEdit.editAccountToPayFrom,
                        onClick: () => setDebitAccountModalOpen(true),
                    }}
                    show={loanDetails?.debitAccountNumber && featureToggles.enableEditLoan}
                    formatter={formatAccountNumber}
                    value={loanDetails?.debitAccountNumber}
                    dataTrackId={loansTrackingIDs.loanDetails}
                    dataTrackLinkname={translated.loanEdit.editAccountToPayFrom}
                    icon={IconTypes.PENN_24}
                />
                <DetailsListItem
                    label={translated.loanDetails.downpaymentDay}
                    button={{ title: translated.loanEdit.editDay, onClick: () => setPaymentDateModalOpen(true) }}
                    show={typeof loanDetails?.nextPaymentDay === 'number' && featureToggles.enableEditLoan}
                    value={eachYearOrMonthText}
                    dataTrackId={loansTrackingIDs.loanDetails}
                    dataTrackLinkname={translated.loanEdit.editDay}
                    icon={IconTypes.PENN_24}
                />
                <DetailsListItem<number>
                    show={typeof loanDetails?.nextPaymentAmount === 'number'}
                    label={translated.loanDetails.installmentAmount}
                    formatter={(value) => currencyFormatter(value, account?.currency ?? 'NOK')}
                    value={loanDetails?.nextPaymentAmount}
                />
            </DetailsList>
            {typeof loanDetails?.nextPaymentDay === 'number' &&
                targetAccountId &&
                featureToggles.enableEditLoan &&
                loanDetails.nextPaymentDate && (
                    <EditLoanPaymentDay
                        accountId={targetAccountId}
                        currentDay={loanDetails.nextPaymentDay}
                        nextDate={getZonedDate(loanDetails.nextPaymentDate)}
                        show={paymentDateModalOpen}
                        setShow={setPaymentDateModalOpen}
                        onSubmit={onSubmit}
                    />
                )}
            {loanDetails?.debitAccountNumber && targetAccountId && featureToggles.enableEditLoan && (
                <EditLoanDebitAccountModal
                    accountId={targetAccountId}
                    currentAccount={loanDetails.debitAccountNumber}
                    show={debitAccountModalOpen}
                    setShow={setDebitAccountModalOpen}
                    onSubmit={onSubmit}
                />
            )}
        </Content>
    );
};

export default LoanDetails;
